import { AlertBannerType, ExAlertBanner } from '@boomi/exosphere';
import classnames from 'classnames';
import { type ChangeEvent, useEffect, useState } from 'react';
import ButtonIcon from '../../../../../../ts/components/buttons/ButtonIcon';
import { useMapElement } from '../../../../../../ts/components/flow/elementConfigurations/contextProviders/MapElementProvider';
import Loader from '../../../../../../ts/components/loader/Loader';
import ValueSelectorModal from '../../../../../../ts/components/values/selector/ValueSelectorModal';
import { criteriaOptions } from '../../../../../../ts/config/rules';
import { getValueList } from '../../../../../../ts/sources/value';
import type { CriteriaType } from '../../../../../types/generic';
import type { Rule } from '../../../../../types/graph';
import type { ValueElementIdReferenceAPI } from '../../../../../types/value';
import { useNotificationDispatch } from '../../../../notifications/NotificationProvider';

type Props = {
    rule: Rule;
    setRule: (rule: Rule) => void;
    removeRule: () => void;
    hasSubmitted: boolean;
};

const BusinessRule = ({ rule, setRule, removeRule, hasSubmitted }: Props) => {
    const { container } = useMapElement();
    const dispatchNotification = useNotificationDispatch();

    const [loadingError, setLoadingError] = useState(null);
    const [selectedLeftReference, setSelectedLeftReference] =
        useState<ValueElementIdReferenceAPI | null>(null);
    const [selectedRightReference, setSelectedRightReference] =
        useState<ValueElementIdReferenceAPI | null>(null);

    const validateCriteria = (
        leftReference: ValueElementIdReferenceAPI,
        criteriaType: CriteriaType | null,
        newRule: Rule,
    ) => {
        const allowedCriteriaList = calculateAllowedCriteria(leftReference);

        if (
            criteriaType &&
            allowedCriteriaList.map((entry) => entry.criteria).includes(criteriaType) === false
        ) {
            return {
                ...newRule,
                criteriaType: null,
            };
        }
        return newRule;
    };

    const validateRightReference = (
        leftReference: ValueElementIdReferenceAPI | null,
        criteriaType: CriteriaType | null,
        newRule: Rule,
    ) => {
        const allowedRightContentType = calculateAllowedRightContentType(
            leftReference,
            criteriaType,
        );
        const allowedRightTypeElementId = calculateAllowedRightTypeElementId(
            leftReference,
            criteriaType,
        );
        if (
            selectedRightReference &&
            // If there is a right reference selected
            (selectedRightReference?.contentType !== allowedRightContentType ||
                // and the right reference is either the wrong contentType
                (allowedRightTypeElementId !== null &&
                    // or, if there is a type element filter applied
                    selectedRightReference.typeElementId !== allowedRightTypeElementId))
            // and the right reference doesn't match this
        ) {
            // then the right reference is not longer valid and must be cleared
            setSelectedRightReference(null);
            return {
                ...newRule,
                rightValueElementToReferenceId: null,
            };
        }
        return newRule;
    };

    const onLeftValueToChangeSelect = (value: ValueElementIdReferenceAPI | null) => {
        setSelectedLeftReference(value);

        if (value) {
            let newRule: Rule = {
                ...rule,
                leftValueElementToReferenceId: {
                    id: value?.id,
                    typeElementPropertyId: value?.typeElementPropertyId,
                    command: null,
                },
            };

            // If the left value has a selection
            // validate that the criteria picked is still valid
            newRule = validateCriteria(value, newRule?.criteriaType, newRule);
            // validate that the right value picked is still valid
            newRule = validateRightReference(value, newRule?.criteriaType, newRule);

            setRule(newRule);
        } else {
            // If the left value is being cleared
            // then clear the criteria and clear the right value
            setSelectedRightReference(null);
            setRule({
                ...rule,
                leftValueElementToReferenceId: null,
                criteriaType: null,
                rightValueElementToReferenceId: null,
            });
        }
    };

    const onCriteriaChange = (selection: ChangeEvent<HTMLSelectElement>) => {
        const newCriteriaType = selection?.target?.value as CriteriaType;

        // validate that the right value picked is still valid
        const newRule = validateRightReference(selectedLeftReference, newCriteriaType, {
            ...rule,
            criteriaType: newCriteriaType,
        });

        setRule(newRule);
    };

    const onRightValueToChangeSelect = (value: ValueElementIdReferenceAPI | null) => {
        setSelectedRightReference(value);
        setRule({
            ...rule,
            rightValueElementToReferenceId: {
                id: value ? value.id : null,
                typeElementPropertyId: value ? value.typeElementPropertyId : null,
                command: null,
            },
        });
    };

    const leftValueId = rule.leftValueElementToReferenceId?.id ?? null;
    const leftValuePropertyId = rule.leftValueElementToReferenceId?.typeElementPropertyId ?? null;
    const rightValueId = rule.rightValueElementToReferenceId?.id ?? null;
    const rightValuePropertyId = rule.rightValueElementToReferenceId?.typeElementPropertyId ?? null;

    const isLoadingLeftRule = selectedLeftReference === null && leftValueId !== null;
    const isLoadingRightRule = selectedRightReference === null && rightValueId !== null;
    const isLoading = isLoadingLeftRule || isLoadingRightRule;

    const convertSelectedIdsIntoReference = (
        valueId: string | null,
        propertyId: string | null,
        referenceSetter: (value: ValueElementIdReferenceAPI | null) => void,
        valueReferences: ValueElementIdReferenceAPI[],
    ) => {
        if (valueId) {
            const reference = valueReferences.find(
                (vr) => vr.id === valueId && vr.typeElementPropertyId === propertyId,
            );

            if (reference) {
                referenceSetter(reference);
            } else {
                if (propertyId) {
                    throw new Error(
                        `Could not find value reference with ID: ${valueId} and property ID: ${propertyId}`,
                    );
                }
                throw new Error(`Could not find value reference with ID: ${valueId}`);
            }
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        if (isLoading && loadingError === null) {
            // load left and right value info from ids given

            const ids: string[] = [];
            if (leftValueId) {
                ids.push(leftValueId);
            }
            if (rightValueId) {
                ids.push(rightValueId);
            }

            const getValueReferences = async () => {
                const responseValueReferences = await getValueList({
                    id: ids,
                }).catch((error) => {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: {
                            error,
                        },
                    });
                    setLoadingError(typeof error === 'string' ? error : error.message);
                });

                if (responseValueReferences) {
                    convertSelectedIdsIntoReference(
                        leftValueId,
                        leftValuePropertyId,
                        setSelectedLeftReference,
                        responseValueReferences,
                    );
                    convertSelectedIdsIntoReference(
                        rightValueId,
                        rightValuePropertyId,
                        setSelectedRightReference,
                        responseValueReferences,
                    );
                }
            };

            getValueReferences();
        }
    }, [leftValueId, selectedLeftReference, rightValueId, selectedRightReference, loadingError]);

    if (loadingError) {
        return (
            <ExAlertBanner type={AlertBannerType.ERROR} open>
                {loadingError}
            </ExAlertBanner>
        );
    }

    if (isLoading) {
        return <Loader message="Loading business rule..." />;
    }

    const calculateAllowedRightContentType = (
        leftValue: ValueElementIdReferenceAPI | null,
        criteria: CriteriaType | null,
    ) =>
        criteria === 'IS_EMPTY'
            ? // If the criteria type is "IS_EMPTY", then the filter should just be a contentType filter of boolean
              'ContentBoolean'
            : // Otherwise, all filters should match the contentType of the left side
              (leftValue?.contentType ?? null);

    const calculateAllowedRightTypeElementId = (
        leftValue: ValueElementIdReferenceAPI | null,
        criteria: CriteriaType | null,
    ) =>
        criteria === 'IS_EMPTY'
            ? // If the criteria type is "IS_EMPTY", then the filter should not have a type id filter
              null
            : ['ContentList', 'ContentObject'].includes(leftValue?.contentType ?? '')
              ? // If the let value was an Object or List, then the type should be the same for this right hand side
                (leftValue?.typeElementPropertyTypeElementId ?? leftValue?.typeElementId ?? null)
              : // Otherwise no type filter
                null;

    const calculateAllowedCriteria = (leftValue: ValueElementIdReferenceAPI) => {
        if (leftValue.contentType) {
            return criteriaOptions[
                leftValue.parentContentType === 'ContentList'
                    ? 'ContentListProperty'
                    : leftValue.contentType
            ];
        }

        return [];
    };

    return (
        <div className="business-rule">
            <div className="left-value-selector" data-testid="leftValueSelector">
                <ValueSelectorModal
                    value={selectedLeftReference}
                    includeSystemValues={true}
                    onChangeAsValueReference={onLeftValueToChangeSelect}
                    container={container}
                />
                {hasSubmitted && !selectedLeftReference ? (
                    <span className="help-block error-state">Please select a value.</span>
                ) : null}
            </div>
            <div className={classnames('criteria-select', { hidden: !selectedLeftReference })}>
                <select
                    className="form-control"
                    value={rule?.criteriaType ?? ''}
                    onChange={onCriteriaChange}
                    data-testid="ruleCriteriaSelect"
                >
                    {selectedLeftReference
                        ? calculateAllowedCriteria(selectedLeftReference).map(
                              ({ label, criteria }) => (
                                  <option value={criteria} key={criteria}>
                                      {label}
                                  </option>
                              ),
                          )
                        : []}
                    {rule?.criteriaType ? null : (
                        <option value="" key="" disabled>
                            --Please Select--
                        </option>
                    )}
                </select>
                {hasSubmitted && !rule?.criteriaType ? (
                    <span className="help-block error-state">Please select a criteria.</span>
                ) : null}
            </div>
            <div
                className={classnames('right-value-selector', { hidden: !rule?.criteriaType })}
                data-testid="rightValueSelector"
            >
                <ValueSelectorModal
                    value={selectedRightReference}
                    includeSystemValues={true}
                    onChangeAsValueReference={onRightValueToChangeSelect}
                    contentType={calculateAllowedRightContentType(
                        selectedLeftReference,
                        rule?.criteriaType,
                    )}
                    typeElementId={calculateAllowedRightTypeElementId(
                        selectedLeftReference,
                        rule?.criteriaType,
                    )}
                    container={container}
                />
                {hasSubmitted && !selectedRightReference ? (
                    <span className="help-block error-state">Please select a value.</span>
                ) : null}
            </div>
            <ButtonIcon
                className="margin-left-small danger"
                glyph="Delete"
                aria-label="Delete Rule"
                title="Delete Rule"
                onClick={removeRule}
                iconClass="icon-medium"
            />
        </div>
    );
};

export default BusinessRule;
